import {
  Container,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
} from "@mantine/core";
import { useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  useInsurancePlanTypesAdmin,
  useCreateInsurancePlanType,
  useUpdateInsurancePlanType,
  useToggleInsurancePlanTypeActive,
} from "../../../hooks/admin/useInsurancePlanTypes";
import AdminDataTable, {
  AdminColumn,
} from "../../../components/admin/shared/AdminDataTable";
import InsurancePlanTypeForm, {
  InsurancePlanTypeFormValues,
} from "../../../components/admin/insurance-plan-types/InsurancePlanTypeForm";
import { InsurancePlanType } from "../../../services/admin/insurance-plan-types.service";

export default function InsurancePlanTypesAdminPage() {
  const [opened, { open, close }] = useDisclosure(false);
  const [editingItem, setEditingItem] = useState<InsurancePlanType | null>(
    null
  );

  const { data: planTypes = [], isLoading } = useInsurancePlanTypesAdmin();
  const createPlanType = useCreateInsurancePlanType();
  const updatePlanType = useUpdateInsurancePlanType();
  const toggleActive = useToggleInsurancePlanTypeActive();

  const form = useForm<InsurancePlanTypeFormValues>({
    initialValues: {
      type: "",
      description: "",
      isActive: true,
    },
  });

  const handleAdd = () => {
    setEditingItem(null);
    form.reset();
    open();
  };

  const handleEdit = (item: InsurancePlanType) => {
    setEditingItem(item);
    form.setValues(item as any);
    open();
  };

  const handleSubmit = async (values: InsurancePlanTypeFormValues) => {
    if (editingItem) {
      await updatePlanType.mutateAsync({
        id: editingItem.id,
        ...values,
      });
    } else {
      await createPlanType.mutateAsync(values);
    }
    close();
    form.reset();
  };

  const handleToggleActive = async (item: InsurancePlanType) => {
    await toggleActive.mutateAsync({
      id: item.id,
      isActive: !item.isActive,
    });
  };

  const columns: AdminColumn<InsurancePlanType>[] = [
    {
      key: "type",
      label: "Plan Type",
      sortable: true,
      render: (item) => (
        <Text size="sm" fw={600}>
          {item.type}
        </Text>
      ),
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      render: (item) => (
        <Text size="sm" lineClamp={2}>
          {item.description || "—"}
        </Text>
      ),
    },
    {
      key: "isActive",
      label: "Status",
      width: "100px",
      render: (item) => (
        <Badge color={item.isActive ? "green" : "red"} variant="light">
          {item.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2}>Insurance Plan Types</Title>
          <Text size="sm" c="dimmed" mt="xs">
            Manage insurance plan types (PPO, HMO, EPO, POS, Medicare, etc.)
          </Text>
        </div>

        <AdminDataTable
          data={planTypes}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onToggleActive={handleToggleActive}
          searchPlaceholder="Search plan types..."
          emptyMessage="No insurance plan types found"
          getId={(item) => item.id}
          getIsActive={(item) => item.isActive}
        />
      </Stack>

      <Modal
        opened={opened}
        onClose={close}
        title={
          editingItem
            ? "Edit Insurance Plan Type"
            : "Add New Insurance Plan Type"
        }
        size="md"
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          <InsurancePlanTypeForm
            initialValues={editingItem || undefined}
            onSubmit={handleSubmit}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createPlanType.isPending || updatePlanType.isPending}
              onClick={() => {
                const form = document.querySelector("form");
                if (form) form.requestSubmit();
              }}
            >
              {editingItem ? "Update" : "Create"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
